(function() {
  const CHAT_API = 'https://dolphin-ai-agents.vercel.app/api/chat';
  const STORAGE_KEY = 'dolphin_chat_v4'; // Multi-conversation support
  const CONVERSATIONS_KEY = 'dolphin_conversations_v1';
  const MAX_CONVERSATIONS = 20;
  const CONVERSATION_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days
  const DEFAULT_CONVERSATION_TITLE = 'Ask me anything';
  
  const bubble = document.getElementById('dolphin-chat-bubble');
  const overlay = document.getElementById('dolphin-chat-overlay');
  const chatWindow = document.getElementById('dolphin-chat-window');
  const closeBtn = document.getElementById('dolphin-chat-close');
  const messagesContainer = document.getElementById('dolphin-chat-messages');
  const input = document.getElementById('dolphin-chat-input');
  const fileInput = document.getElementById('dolphin-chat-file');
  const attachBtn = document.getElementById('dolphin-chat-attach');
  const sendBtn = document.getElementById('dolphin-chat-send');
  const newChatBtn = document.getElementById('dolphin-chat-new');
  const conversationSelector = document.getElementById('dolphin-chat-conversation-selector');
  const conversationTrigger = document.getElementById('dolphin-chat-conversation-trigger');
  const conversationMenu = document.getElementById('dolphin-chat-conversation-menu');
  const conversationList = document.getElementById('dolphin-chat-conversation-list');
  const currentConversationEl = document.getElementById('dolphin-chat-current-conversation');
  const imagePreview = document.getElementById('dolphin-chat-image-preview');
  const imageThumb = document.getElementById('dolphin-chat-image-thumb');
  const imageName = document.getElementById('dolphin-chat-image-name');
  const imageRemove = document.getElementById('dolphin-chat-image-remove');

  const resizer = document.getElementById('dolphin-chat-resize');
  const dock = window.DGChatDock; // snippets/dg-chat-dock.liquid

  const embedMount = document.getElementById('dolphin-chat-embed');
  const isEmbedded = !!embedMount;
  
  let conversationHistory = [];
  let currentConversationId = null;
  let allConversations = [];
  let isLoading = false;
  let previousBodyOverflow = null;
  let sheetTimer = 0;
  let hasInitialized = false;

  function ensureInitialized() {
    if (hasInitialized) return;
    hasInitialized = true;
    initConversations();
    updateCurrentConversationLabel();
    renderConversationMenu();
    resizeChatInput();
  }

  function setupEmbeddedMode() {
    if (!isEmbedded || !embedMount) return;

    try {
      document.body.classList.add('dolphin-chat-embed-mode');
    } catch (e) {}

    if (chatWindow) {
      chatWindow.classList.add('dolphin-chat-embedded');
      chatWindow.classList.add('open');
    }

    if (overlay) {
      overlay.classList.remove('open');
      overlay.setAttribute('aria-hidden', 'true');
    }

    if (bubble) {
      bubble.setAttribute('aria-hidden', 'true');
    }

    if (embedMount && chatWindow) {
      embedMount.innerHTML = '';
      embedMount.appendChild(chatWindow);
    }

    // Remove the pre-rendered welcome message so it only appears once the embed is viewed.
    if (messagesContainer) {
      messagesContainer.innerHTML = '';
    }
  }

  function initEmbeddedOnScroll() {
    if (!isEmbedded || !embedMount) return;

    if (!('IntersectionObserver' in window)) {
      ensureInitialized();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries || entries.length === 0) return;
      const isVisible = entries.some(entry => entry.isIntersecting);
      if (!isVisible) return;
      observer.disconnect();
      ensureInitialized();
    }, { threshold: 0.25 });

    observer.observe(embedMount);
  }
  
  // Generate unique ID
  function generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
  }
  
  // Get preview text from conversation
  function getConversationPreview(messages) {
    const userMsg = messages.find(m => m.role === 'user');
    if (userMsg) {
      const text = userMsg.content.substring(0, 50);
      return text.length < userMsg.content.length ? text + '...' : text;
    }
    return DEFAULT_CONVERSATION_TITLE;
  }
  
  // Format date for display
  function formatDate(timestamp) {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60 * 1000) return 'Just now';
    if (diff < 60 * 60 * 1000) return Math.floor(diff / 60000) + 'm ago';
    if (diff < 24 * 60 * 60 * 1000) return Math.floor(diff / 3600000) + 'h ago';
    if (diff < 7 * 24 * 60 * 60 * 1000) return Math.floor(diff / 86400000) + 'd ago';
    return date.toLocaleDateString();
  }

  function getCurrentConversationTitle(conversationId) {
    const conv = allConversations.find(c => c.id === conversationId);
    if (!conv) return DEFAULT_CONVERSATION_TITLE;

    return getConversationPreview(conv.messages || []);
  }

  function updateCurrentConversationLabel() {
    if (!currentConversationEl) return;

    if (!currentConversationId) {
      currentConversationEl.textContent = DEFAULT_CONVERSATION_TITLE;
      return;
    }

    currentConversationEl.textContent = getCurrentConversationTitle(currentConversationId);
  }
  
  // Load all conversations from localStorage
  function loadAllConversations() {
    try {
      const saved = localStorage.getItem(CONVERSATIONS_KEY);
      if (saved) {
        allConversations = JSON.parse(saved);
        // Remove expired conversations
        const now = Date.now();
        allConversations = allConversations.filter(c => now - c.updatedAt < CONVERSATION_TTL);
        saveAllConversations();
      }
    } catch (e) {
      console.error('Error loading conversations:', e);
      allConversations = [];
    }
    return allConversations;
  }
  
  // Save all conversations to localStorage
  function saveAllConversations() {
    try {
      // Keep only the most recent MAX_CONVERSATIONS
      if (allConversations.length > MAX_CONVERSATIONS) {
        allConversations = allConversations
          .sort((a, b) => b.updatedAt - a.updatedAt)
          .slice(0, MAX_CONVERSATIONS);
      }
      localStorage.setItem(CONVERSATIONS_KEY, JSON.stringify(allConversations));
    } catch (e) {
      console.error('Error saving conversations:', e);
    }
  }
  
  // Load specific conversation
  function loadConversation(conversationId) {
    const conv = allConversations.find(c => c.id === conversationId);
    if (conv) {
      currentConversationId = conv.id;
      conversationHistory = conv.messages || [];
      clearMessagesUI();
      // Restore messages to UI
      conversationHistory.forEach(msg => {
        addMessageToUI(msg.content, msg.role === 'user', msg.products || null, msg.actions || null);
      });
      // If this conversation has no saved messages, show the default welcome message
      if (conversationHistory.length === 0) {
        addWelcomeMessage();
      }
      // Save current conversation ID
      localStorage.setItem(STORAGE_KEY, conversationId);
      updateCurrentConversationLabel();
      return true;
    }
    return false;
  }
  
  // Create new conversation
  function createNewConversation(opts) {
    const inquiry = !!(opts && opts.inquiry);
    currentConversationId = generateId();
    conversationHistory = [];
    clearMessagesUI();
    // An inquiry opens on the agent's own first message instead (see startInquiry)
    if (!inquiry) addWelcomeMessage();
    
    // Add to conversations list
    allConversations.unshift({
      id: currentConversationId,
      messages: [],
      inquiry: inquiry,
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
    saveAllConversations();
    localStorage.setItem(STORAGE_KEY, currentConversationId);
    updateCurrentConversationLabel();
    
    return currentConversationId;
  }
  
  // Save current conversation
  function saveConversation() {
    if (!currentConversationId) return;
    
    try {
      const idx = allConversations.findIndex(c => c.id === currentConversationId);
      const toSave = conversationHistory.slice(-15); // Limit to last 15 messages
      
      if (idx >= 0) {
        allConversations[idx].messages = toSave;
        allConversations[idx].updatedAt = Date.now();
      } else {
        allConversations.unshift({
          id: currentConversationId,
          messages: toSave,
          createdAt: Date.now(),
          updatedAt: Date.now()
        });
      }
      saveAllConversations();
      updateCurrentConversationLabel();
    } catch (e) {
      console.error('Error saving conversation:', e);
    }
  }
  
  // Delete conversation
  function deleteConversation(id, event) {
    event.stopPropagation();
    const wasCurrentConversation = id === currentConversationId;
    allConversations = allConversations.filter(c => c.id !== id);

    // If deleted current conversation, start a new one (createNewConversation saves)
    if (wasCurrentConversation) {
      createNewConversation();
    } else {
      saveAllConversations();
    }

    renderConversationMenu();
    updateCurrentConversationLabel();
  }
  
  // Clear messages UI
  function clearMessagesUI() {
    messagesContainer.innerHTML = '';
  }
  
  const SUGGESTION_PILLS = [
    { label: 'Pricing', message: 'Can you help me with pricing on a piece?' },
    { label: 'Shipping', message: 'How much is shipping?' },
    { label: 'Tell me about the artist', message: 'Tell me about this artist' },
    { label: 'Request a quote', message: 'Can I get a quote?' },
  ];

  function addWelcomeMessage() {
    const msg = document.createElement('div');
    msg.className = 'dolphin-chat-message bot animate-in';
    msg.textContent = 'Welcome to Dolphin Galleries. What can I help you with today?';
    messagesContainer.appendChild(msg);

    const pillsContainer = document.createElement('div');
    pillsContainer.className = 'dolphin-chat-suggestions';
    pillsContainer.id = 'dolphin-chat-suggestions';

    SUGGESTION_PILLS.forEach(function(pill) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dolphin-chat-pill';
      btn.textContent = pill.label;
      btn.setAttribute('data-msg', pill.message);
      pillsContainer.appendChild(btn);
    });

    messagesContainer.appendChild(pillsContainer);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }

  // The welcome line and suggestions fade in via CSS, but that ran once at page load
  // with the chat closed. Restart it whenever the chat is opened.
  function playIntro() {
    messagesContainer.classList.add('intro-reset');
    void messagesContainer.offsetWidth;
    messagesContainer.classList.remove('intro-reset');
  }

  // Reveal a freshly rendered answer word by word. Returns how long that takes (ms).
  function streamIn(el) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    const total = nodes.reduce((n, node) => n + (node.nodeValue.match(/\S+/g) || []).length, 0);
    if (!total) return 0;
    const step = Math.min(35, 2200 / total); // long answers still finish in ~2s
    let i = 0;
    nodes.forEach((node) => {
      const frag = document.createDocumentFragment();
      node.nodeValue.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
        const word = document.createElement('span');
        word.className = 'dolphin-chat-word';
        word.style.animationDelay = Math.round(i++ * step) + 'ms';
        word.textContent = part;
        frag.appendChild(word);
      });
      node.parentNode.replaceChild(frag, node);
    });
    return Math.round(total * step);
  }

  // Also clears the contact form: both belong to the answer they sit under.
  function removeSuggestionPills() {
    var pills = messagesContainer.querySelectorAll('.dolphin-chat-suggestions, .dolphin-chat-contact-form');
    pills.forEach(function(el) { el.remove(); });
  }

  // 2026-10-23: input boxes under an answer that asks for the customer's details
  // (e.g. "Request a quote"). The chat API says which, in order (email first).
  // Email is the only required one; sending posts one ordinary customer message
  // such as "Email: jane@x.com, Name: Jane".
  const CONTACT_INPUTS = {
    email: { label: 'Email', type: 'email', autocomplete: 'email', inputmode: 'email' },
    name: { label: 'Name', type: 'text', autocomplete: 'name' },
    phone: { label: 'Phone', type: 'tel', autocomplete: 'tel', inputmode: 'tel' }
  };

  function addContactForm(fields, delayMs) {
    if (!Array.isArray(fields)) return;
    const wanted = fields.filter(function(f, i) { return CONTACT_INPUTS[f] && fields.indexOf(f) === i; });
    if (wanted.length === 0) return;

    const form = document.createElement('form');
    form.className = 'dolphin-chat-contact-form';
    form.noValidate = true;
    form.style.animationDelay = (delayMs || 0) + 'ms';
    const emailAsked = wanted.indexOf('email') !== -1;
    const known = withContact(customerInfo);
    const inputs = {};
    wanted.forEach(function(field) {
      const spec = CONTACT_INPUTS[field];
      const optional = emailAsked && field !== 'email';
      const label = document.createElement('label');
      label.textContent = spec.label + (optional ? ' (optional)' : '');
      const el = document.createElement('input');
      el.type = spec.type;
      el.name = field;
      el.autocomplete = spec.autocomplete;
      if (spec.inputmode) el.inputMode = spec.inputmode;
      if (field === 'email') el.required = true;
      if (known[field]) el.value = String(known[field]).trim();
      label.appendChild(el);
      form.appendChild(label);
      inputs[field] = el;
    });

    const error = document.createElement('p');
    error.className = 'dolphin-chat-contact-error';
    error.setAttribute('role', 'alert');
    form.appendChild(error);

    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.textContent = 'Send';
    form.appendChild(submit);

    form.addEventListener('submit', function(e) {
      e.preventDefault();
      if (isLoading) return;
      const email = emailAsked ? inputs.email.value.trim() : '';
      if (emailAsked && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        error.textContent = 'Please enter a valid email address.';
        inputs.email.focus();
        return;
      }
      const parts = [];
      wanted.forEach(function(field) {
        const value = inputs[field].value.trim();
        if (value) parts.push(CONTACT_INPUTS[field].label + ': ' + value);
      });
      if (parts.length === 0) {
        error.textContent = 'Please fill this in.';
        inputs[wanted[0]].focus();
        return;
      }
      if (inputs.name && inputs.name.value.trim()) rememberContact({ name: inputs.name.value.trim() });
      // Anything they had started typing in the message box goes along with it
      const note = input.value.trim();
      input.value = parts.join(', ') + (note ? '\n' + note : '');
      sendMessage();
    });

    messagesContainer.appendChild(form);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }

  // Follow-up suggestions under an answer (sent by the API with every reply).
  // Tapping one sends it as the customer's next message, same as the welcome pills.
  function addFollowUpPills(suggestions, delayMs) {
    if (!Array.isArray(suggestions) || suggestions.length === 0) return;
    removeSuggestionPills();
    const pillsContainer = document.createElement('div');
    pillsContainer.className = 'dolphin-chat-suggestions';
    suggestions.forEach(function(text, i) {
      if (typeof text !== 'string' || !text.trim()) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dolphin-chat-pill';
      btn.textContent = text;
      btn.setAttribute('data-msg', text);
      // Appear once the answer has finished streaming in, one after another
      btn.style.animationDelay = ((delayMs || 0) + i * 70) + 'ms';
      pillsContainer.appendChild(btn);
    });
    if (pillsContainer.children.length > 0) messagesContainer.appendChild(pillsContainer);
  }
  
  function renderConversationMenu() {
    if (!conversationList) return;

    conversationList.innerHTML = '';

    if (allConversations.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'dolphin-chat-conversation-empty';
      empty.textContent = 'No conversation history yet';
      conversationList.appendChild(empty);
      return;
    }

    const sorted = allConversations
      .slice()
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

    sorted.forEach(conv => {
      const isActive = conv.id === currentConversationId;
      const item = document.createElement('div');
      item.className = 'dolphin-chat-conversation-item' + (isActive ? ' active' : '');

      const preview = getConversationPreview(conv.messages || []);
      const dateLabel = formatDate(conv.updatedAt || Date.now());

      const row = document.createElement('div');
      row.className = 'dolphin-chat-conversation-item-row';

      const previewEl = document.createElement('p');
      previewEl.className = 'dolphin-chat-conversation-item-preview';
      previewEl.textContent = preview;
      row.appendChild(previewEl);

      if (isActive) {
        const badge = document.createElement('span');
        badge.className = 'dolphin-chat-conversation-badge';
        badge.textContent = 'Current';
        row.appendChild(badge);
      }

      const meta = document.createElement('div');
      meta.className = 'dolphin-chat-conversation-item-meta';

      const dateSpan = document.createElement('span');
      dateSpan.textContent = dateLabel;

      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'dolphin-chat-conversation-delete';
      deleteBtn.type = 'button';
      deleteBtn.textContent = 'Delete';
      deleteBtn.setAttribute('aria-label', 'Delete conversation');
      deleteBtn.addEventListener('click', (e) => {
        deleteConversation(conv.id, e);
      });

      meta.appendChild(dateSpan);
      meta.appendChild(deleteBtn);

      item.appendChild(row);
      item.appendChild(meta);

      item.addEventListener('click', () => {
        loadConversation(conv.id);
        closeConversationMenu();
      });

      conversationList.appendChild(item);
    });
  }

  function openConversationMenu() {
    if (!conversationSelector || !conversationTrigger) return;
    renderConversationMenu();
    conversationSelector.classList.add('open');
    conversationTrigger.setAttribute('aria-expanded', 'true');
  }

  function closeConversationMenu() {
    if (!conversationSelector || !conversationTrigger) return;
    conversationSelector.classList.remove('open');
    conversationTrigger.setAttribute('aria-expanded', 'false');
  }

  function toggleConversationMenu() {
    if (!conversationSelector) return;
    if (conversationSelector.classList.contains('open')) {
      closeConversationMenu();
      return;
    }
    openConversationMenu();
  }
  
  function normalizeLegacyMessages(messages) {
    if (!Array.isArray(messages)) return [];

    return messages
      .filter(m =>
        m &&
        (m.role === 'user' || m.role === 'assistant') &&
        typeof m.content === 'string' &&
        m.content.trim() !== ''
      )
      .slice(-15)
      .map(m => ({
        role: m.role,
        content: m.content,
        products: Array.isArray(m.products) ? m.products.slice(0, 6) : null,
        actions: Array.isArray(m.actions) ? m.actions.slice(0, 4) : null
      }));
  }

  // One-time migration from the old single-thread storage format (dolphin_chat_v3)
  function tryMigrateLegacyConversation() {
    const LEGACY_KEY = 'dolphin_chat_v3';

    const legacyRaw = localStorage.getItem(LEGACY_KEY);
    if (!legacyRaw) return false;

    try {
      const legacy = JSON.parse(legacyRaw);
      const legacyTimestamp =
        legacy && typeof legacy.timestamp === 'number' ? legacy.timestamp : Date.now();

      // Ignore very old legacy conversations
      if (Date.now() - legacyTimestamp > CONVERSATION_TTL) {
        localStorage.removeItem(LEGACY_KEY);
        return false;
      }

      const normalized = normalizeLegacyMessages(legacy ? legacy.messages : null);
      if (normalized.length === 0) {
        localStorage.removeItem(LEGACY_KEY);
        return false;
      }

      const legacyConversationId = generateId();
      allConversations.unshift({
        id: legacyConversationId,
        messages: normalized,
        createdAt: legacyTimestamp,
        updatedAt: legacyTimestamp
      });
      saveAllConversations();

      // Remove old key so we don't repeatedly migrate
      localStorage.removeItem(LEGACY_KEY);

      loadConversation(legacyConversationId);
      return true;
    } catch (e) {
      console.error('Error migrating legacy conversation:', e);
      localStorage.removeItem(LEGACY_KEY);
      return false;
    }
  }

  // Initialize conversations
  function initConversations() {
    loadAllConversations();
    
    // Try to load last active conversation
    const lastId = localStorage.getItem(STORAGE_KEY);
    if (lastId && loadConversation(lastId)) {
      return;
    }

    // Migrate legacy single-thread history once (if present)
    if (tryMigrateLegacyConversation()) {
      return;
    }
    
    // Check for recent conversation (within 1 hour)
    const recent = allConversations.find(c => Date.now() - c.updatedAt < 60 * 60 * 1000);
    if (recent && loadConversation(recent.id)) {
      return;
    }
    
    // Start new conversation
    createNewConversation();
  }
  
  // Get page context
  const customerInfo = window.DGChatContext || { currentPage: window.location.pathname, pageType: '' };
  
  function setOpen(isOpen) {
    if (!chatWindow || !overlay || !bubble) return;

    if (isOpen) {
      chatWindow.classList.add('open');
      playIntro();

      // Room to sit beside the page: no scrim, no scroll lock, and it stays open
      // from page to page until it is closed.
      if (dock && dock.canDock()) {
        dock.open();
        input && input.focus();
        return;
      }

      overlay.classList.add('open');
      overlay.setAttribute('aria-hidden', 'false');

      if (previousBodyOverflow === null) {
        previousBodyOverflow = document.body.style.overflow || '';
      }
      document.body.style.overflow = 'hidden';

      // Hide the page behind the sheet once it has finished sliding up (phones only, by CSS).
      clearTimeout(sheetTimer);
      sheetTimer = setTimeout(() => {
        if (overlay.classList.contains('open')) document.documentElement.classList.add('dg-chat-sheet-open');
      }, 330);

      // No auto-focus on a touch screen: the keyboard would come up with the sheet.
      if (input && !('ontouchstart' in window)) input.focus();
      return;
    }

    clearTimeout(sheetTimer);
    document.documentElement.classList.remove('dg-chat-sheet-open');

    chatWindow.classList.remove('open');
    overlay.classList.remove('open');
    overlay.setAttribute('aria-hidden', 'true');
    if (dock) dock.close();

    // Reset mobile viewport overrides
    chatWindow.style.height = '';
    chatWindow.style.top = '';

    if (previousBodyOverflow !== null) {
      document.body.style.overflow = previousBodyOverflow;
      previousBodyOverflow = null;
    }
  }

  if (!isEmbedded) {
    bubble.addEventListener('click', () => {
      ensureInitialized();
      setOpen(true);
    });
    
    closeBtn.addEventListener('click', () => {
      setOpen(false);
    });

    // On a phone the sheet is the whole screen and the overlay is only its white backing;
    // a tap that reaches it (the sheet shrinks for the keyboard) must not close the chat.
    overlay.addEventListener('click', () => {
      if (!window.matchMedia('(max-width: 749px)').matches) setOpen(false);
    });

    document.addEventListener('keydown', (e) => {
      // Docked, the chat is part of the page — only its close button dismisses it.
      if (e.key === 'Escape' && !(dock && dock.width)) setOpen(false);
    });
  }

  messagesContainer.addEventListener('click', function(e) {
    const pill = e.target.closest('.dolphin-chat-pill');
    if (!pill) return;
    ensureInitialized();
    const msg = pill.getAttribute('data-msg') || pill.textContent;
    removeSuggestionPills();
    input.value = msg;
    sendMessage();
  });
  
  // Markdown parser for chat messages
  function parseMarkdown(text) {
    if (!text) return '';
    
    // Escape HTML first
    let html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    
    // Bold: **text** (do this BEFORE bullet processing)
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    
    // Images: ![alt](url) - must do before links
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="dolphin-chat-inline-image" loading="lazy">');
    
    // Plain image URLs (https://...png/jpg/gif/webp)
    html = html.replace(/(https?:\/\/[^\s]+\.(?:png|jpg|jpeg|gif|webp))(?![^<]*>)/gi, '<img src="$1" alt="Screenshot" class="dolphin-chat-inline-image" loading="lazy">');
    
    // Links: [text](url)
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');
    
    // Process line by line for bullets and paragraphs
    const lines = html.split('\n');
    let result = [];
    let inList = false;
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i].trim();
      
      // Check for bullet point: starts with * or - followed by space
      // But NOT if it's just a standalone * (from markdown bold leftovers)
      const bulletMatch = line.match(/^[\*\-]\s+(.+)$/);
      
      if (bulletMatch) {
        if (!inList) {
          result.push('<ul>');
          inList = true;
        }
        result.push('<li>' + bulletMatch[1] + '</li>');
      } else {
        if (inList) {
          result.push('</ul>');
          inList = false;
        }
        if (line) {
          // Don't wrap standalone images in <p> tags
          if (line.startsWith('<img')) {
            result.push(line);
          } else {
            result.push('<p>' + line + '</p>');
          }
        }
      }
    }
    
    if (inList) result.push('</ul>');
    
    return result.join('');
  }
  
  // stream: reveal the answer progressively (new answers only, not restored history)
  function addMessageToUI(text, isUser, products = null, actions = null, stream = false) {
    const msg = document.createElement('div');
    msg.className = 'dolphin-chat-message ' + (isUser ? 'user' : 'bot');
    
    // Parse markdown for bot messages, plain text for user
    if (isUser) {
      msg.textContent = text;
    } else {
      msg.innerHTML = parseMarkdown(text);
    }
    
    messagesContainer.appendChild(msg);
    const streamMs = stream && !isUser ? streamIn(msg) : 0;
    const afterStream = (el) => {
      if (!stream) return;
      el.classList.add('dolphin-chat-after-stream');
      el.style.animationDelay = streamMs + 'ms';
    };
    
    // Add product carousel if products provided
    if (products && products.length > 0) {
      const carousel = document.createElement('div');
      carousel.className = 'dolphin-chat-products';
      products.forEach(p => {
        const card = document.createElement('a');
        card.className = 'dolphin-chat-product';
        card.href = p.url || `/products/${p.handle}`;
        // Same tab: docked, the chat stays open beside the product it just suggested.
        card.innerHTML = `
          <img src="${p.image || '/cdn/shop/files/placeholder.png'}" alt="${p.title}" loading="lazy">
          <div class="dolphin-chat-product-info">
            <p class="dolphin-chat-product-title">${p.title}</p>
            <span class="dolphin-chat-product-price">${p.price}</span>
          </div>
        `;
        carousel.appendChild(card);
      });
      const cards = Array.prototype.slice.call(carousel.children);
      if (cards.length > 4) {
        cards.slice(4).forEach(card => { card.hidden = true; });
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'dolphin-chat-products-more';
        more.textContent = `Show ${cards.length - 4} more`;
        more.addEventListener('click', () => {
          cards.forEach(card => { card.hidden = false; });
          more.remove();
        });
        carousel.appendChild(more);
      }
      afterStream(carousel);
      messagesContainer.appendChild(carousel);
    }
    
    // Add action buttons (cart links, draft orders, etc.)
    if (actions && actions.length > 0) {
      const actionsContainer = document.createElement('div');
      actionsContainer.className = 'dolphin-chat-actions';
      actions.forEach(action => {
        if (action.url) {
          const btn = document.createElement('a');
          btn.className = 'dolphin-chat-action-btn';
          btn.href = action.url;
          btn.target = action.type === 'cart_link' ? '_self' : '_blank';
          
          // Icons for different action types
          let icon = '';
          let label = action.message || 'Click here';
          if (action.type === 'cart_link') {
            icon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>';
            label = 'Add to Cart';
          } else if (action.type === 'draft_order_created') {
            icon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>';
            label = 'View Quote';
          } else if (action.type === 'email_sent') {
            icon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>';
            label = 'Email Sent ✓';
            btn.className += ' secondary';
            btn.href = '#';
            btn.onclick = (e) => e.preventDefault();
          }
          
          btn.innerHTML = icon + label;
          actionsContainer.appendChild(btn);
        }
      });
      if (actionsContainer.children.length > 0) {
        afterStream(actionsContainer);
        messagesContainer.appendChild(actionsContainer);
      }
    }
    
    // A streamed answer is read from its first word, so bring its top into view;
    // everything else pins to the bottom.
    messagesContainer.scrollTop = streamMs
      ? Math.min(messagesContainer.scrollHeight, msg.offsetTop - messagesContainer.offsetTop - 12)
      : messagesContainer.scrollHeight;
    return streamMs;
  }
  
  function showTyping() {
    const typing = document.createElement('div');
    typing.className = 'dolphin-chat-thinking';
    typing.id = 'typing-indicator';
    typing.setAttribute('role', 'status');
    typing.innerHTML = '<span class="dolphin-chat-thinking-dots" aria-hidden="true">' + '<span></span>'.repeat(9) +
      '</span><span class="dolphin-chat-thinking-text">Thinking</span>';
    messagesContainer.appendChild(typing);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }
  
  function hideTyping() {
    const typing = document.getElementById('typing-indicator');
    if (typing) typing.remove();
  }

  function resizeChatInput() {
    if (!input) return;

    const maxHeight = 160;
    input.style.height = 'auto';
    const nextHeight = Math.min(input.scrollHeight, maxHeight);
    input.style.height = `${nextHeight}px`;
    input.style.overflowY = input.scrollHeight > maxHeight ? 'auto' : 'hidden';
    if (sendBtn) sendBtn.classList.toggle('is-ready', input.value.trim() !== '');
  }
  
  // Name, email and phone the customer has given, kept in this browser so they never
  // have to type them again: emails and phone numbers as they type them, names once
  // the chat API confirms them. Sent with every message, in every conversation.
  const CONTACT_KEY = 'dolphin_chat_customer';
  function storedContact() {
    try { return JSON.parse(localStorage.getItem(CONTACT_KEY)) || {}; } catch (e) { return {}; }
  }
  function rememberContact(found) {
    const next = Object.assign({}, storedContact());
    ['name', 'email', 'phone'].forEach(function(k) {
      if (found && typeof found[k] === 'string' && found[k].trim()) next[k] = found[k].trim().slice(0, 120);
    });
    try { localStorage.setItem(CONTACT_KEY, JSON.stringify(next)); } catch (e) {}
  }
  function contactInText(text) {
    const email = text.match(/[a-z0-9._%+-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}/i);
    const phone = text.match(/\+?\(?\d(?:[\s().-]*\d){9,14}/);
    return { email: email ? email[0] : '', phone: phone ? phone[0].trim() : '' };
  }
  function withContact(info) {
    const out = Object.assign({}, info);
    const kept = storedContact();
    ['name', 'email', 'phone'].forEach(function(k) {
      if (!String(out[k] || '').trim() && kept[k]) out[k] = kept[k];
    });
    return out;
  }

  async function sendMessage() {
    ensureInitialized();
    const text = input.value.trim();
    if (!text || isLoading) return;
    rememberContact(contactInText(text));
    
    removeSuggestionPills();
    addMessageToUI(text, true);
    conversationHistory.push({ role: 'user', content: text });
    saveConversation();
    input.value = '';
    resizeChatInput();
    isLoading = true;
    sendBtn.disabled = true;
    showTyping();
    
    try {
      const response = await fetch(CHAT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          conversationHistory: conversationHistory.slice(-20),
          customerInfo: withInquiry(withContact(customerInfo)),
          conversationId: currentConversationId
        })
      });
      
      const data = await response.json();
      hideTyping();
      if (data && data.customer) rememberContact(data.customer);
      
      if (data.response) {
        // Save products and actions with the message for restoration
        const products = data.products || null;
        const actions = data.actions || null;
        const streamMs = addMessageToUI(data.response, false, products, actions, true);
        addContactForm(data.contactFields, streamMs);
        conversationHistory.push({ 
          role: 'assistant', 
          content: data.response,
          products: products ? products.slice(0, 6) : null, // Limit stored products
          actions: actions ? actions.slice(0, 4) : null // Limit stored actions
        });
        saveConversation();
      } else {
        addMessageToUI("Sorry, something went wrong. Try again?", false);
      }
    } catch (err) {
      hideTyping();
      addMessageToUI("Having trouble connecting. Try again or email info@dolphingalleries.com", false);
    }
    
    isLoading = false;
    sendBtn.disabled = false;
    input.focus();
    resizeChatInput();
  }
  
  // Chats opened from an Inquire button stay flagged, so the agent knows on every turn
  // that this customer came in asking about one piece and was offered a call from Steve.
  function withInquiry(info) {
    const conv = allConversations.find(c => c.id === currentConversationId);
    return conv && conv.inquiry ? Object.assign({}, info, { inquiry: true }) : info;
  }

  // The agent speaks first: it asks what they would like to know and offers to have
  // Steve call or email. The wording comes from the chat API so it lives with the
  // agent's instructions; the copy here only covers the API being unreachable.
  const INQUIRY_FALLBACK = {
    response: 'Happy to help. What would you like to know?\n\nIf you would rather talk it through with a person, Steve, our sales director, can call or email you. Just send your name, email, the best phone number, and anything you\'d like him to know.',
    suggestions: ['Price and availability', "What's the lead time?", 'Shipping to me', 'Have Steve contact me']
  };

  async function startInquiry() {
    const conversationId = createNewConversation({ inquiry: true });
    renderConversationMenu();
    isLoading = true;
    sendBtn.disabled = true;
    showTyping();

    let opener = INQUIRY_FALLBACK;
    try {
      const response = await fetch(CHAT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intent: 'inquiry_start',
          message: '',
          conversationHistory: [],
          customerInfo: Object.assign({}, customerInfo, { inquiry: true }),
          conversationId: conversationId
        })
      });
      const data = await response.json();
      if (data && data.response && Array.isArray(data.suggestions)) opener = data;
    } catch (err) {}

    isLoading = false;
    sendBtn.disabled = false;
    // They moved to another chat while this was loading
    if (conversationId !== currentConversationId) return;
    hideTyping();

    const suggestions = opener.suggestions.slice(0, 4);
    const streamMs = addMessageToUI(opener.response, false, null, null, true);
    addFollowUpPills(suggestions, streamMs);
    conversationHistory.push({ role: 'assistant', content: opener.response, suggestions: suggestions });
    saveConversation();
  }

  // Mount embedded chat (Contact page) and lazy-init only once it's viewed
  setupEmbeddedMode();

  if (isEmbedded) {
    initEmbeddedOnScroll();
  } else {
    ensureInitialized();
  }
  
  // Event listeners
  sendBtn.addEventListener('click', sendMessage);
  input.addEventListener('input', resizeChatInput);
  // The box is the field: a click anywhere in it that isn't a button focuses the text.
  const composer = document.getElementById('dolphin-chat-composer');
  if (composer) {
    composer.addEventListener('click', (e) => {
      if (!e.target.closest('button')) input.focus();
    });
  }
  input.addEventListener('keydown', (e) => {
    // Enter = send, Shift+Enter = newline
    if (e.key !== 'Enter') return;

    // Shift+Enter: force insert newline (more reliable than default behavior across themes/scripts)
    if (e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();

      const start = typeof input.selectionStart === 'number' ? input.selectionStart : input.value.length;
      const end = typeof input.selectionEnd === 'number' ? input.selectionEnd : input.value.length;
      const value = input.value || '';

      input.value = value.slice(0, start) + '\n' + value.slice(end);
      input.selectionStart = input.selectionEnd = start + 1;
      resizeChatInput();
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    sendMessage();
  });

  // Image upload functionality
  let selectedImage = null;

  function clearImageSelection() {
    selectedImage = null;
    if (fileInput) fileInput.value = '';
    if (imagePreview) imagePreview.classList.remove('visible');
    if (imageThumb) imageThumb.src = '';
    if (imageName) imageName.textContent = '';
  }

  function showImagePreview(file) {
    if (!file || !imagePreview || !imageThumb || !imageName) return;
    
    selectedImage = file;
    imageName.textContent = file.name;
    
    const reader = new FileReader();
    reader.onload = function(e) {
      imageThumb.src = e.target.result;
      imagePreview.classList.add('visible');
    };
    reader.readAsDataURL(file);
  }

  if (attachBtn && fileInput) {
    attachBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      fileInput.click();
    });

    fileInput.addEventListener('change', function() {
      const file = this.files && this.files[0];
      if (file) {
        showImagePreview(file);
      }
    });
  }

  if (imageRemove) {
    imageRemove.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      clearImageSelection();
    });
  }
  
  // New chat button
  newChatBtn.addEventListener('click', () => {
    ensureInitialized();
    createNewConversation();
    closeConversationMenu();
    input.focus();
  });
  
  // Current chat dropdown (hover opens via CSS, click toggles for mobile)
  if (conversationSelector) {
    conversationSelector.addEventListener('mouseenter', () => {
      ensureInitialized();
      renderConversationMenu();
    });
  }

  if (conversationTrigger) {
    conversationTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      ensureInitialized();
      toggleConversationMenu();
    });
  }

  document.addEventListener('click', (e) => {
    if (!conversationSelector) return;
    if (!conversationSelector.classList.contains('open')) return;

    if (!conversationSelector.contains(e.target)) {
      closeConversationMenu();
    }
  });

  // (Initialization handled above for embedded vs floating modes.)

  // Mobile Safari: resize chat to visual viewport when keyboard opens/closes
  (function setupMobileKeyboardHandler() {
    if (!window.visualViewport || isEmbedded) return;
    // Only run on mobile touch devices
    if (!('ontouchstart' in window) || window.innerWidth > 749) return;

    let rafId = null;

    // How tall the visible area is with the keyboard up. Remembered from the last time it
    // was open on this device; before that, a guess that errs on the short side.
    const KEYBOARD_KEY = 'dolphin_chat_keyboard';
    let known = null;
    try { known = JSON.parse(localStorage.getItem(KEYBOARD_KEY)); } catch (e) {}
    function heightWithKeyboard() {
      return known && known.w === window.innerWidth ? known.h : Math.round(window.innerHeight * 0.46);
    }
    function keepLatestInView() {
      if (messagesContainer) messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }

    // iOS never resizes the page for the keyboard: it shoves the whole screen upward to
    // reveal the field, and only afterwards can the sheet be refitted — so the header flew
    // off the top and snapped back. Instead, on a tap the sheet is shrunk first and the
    // field focused second, all inside the tap: by the time iOS looks, the field is already
    // above where the keyboard will be and there is nothing to shove.
    // The tap's own click is cancelled — the field has moved out from under the finger, so
    // that click would land on whatever is there now.
    const composerEl = document.getElementById('dolphin-chat-composer');
    if (composerEl) {
      let moved = false;
      composerEl.addEventListener('touchstart', function() { moved = false; }, { passive: true });
      composerEl.addEventListener('touchmove', function() { moved = true; }, { passive: true });
      composerEl.addEventListener('touchend', function(e) {
        if (moved || !chatWindow.classList.contains('open')) return;
        if (document.activeElement === input || e.target.closest('button')) return;
        e.preventDefault();
        chatWindow.style.top = '0px';
        chatWindow.style.height = heightWithKeyboard() + 'px';
        keepLatestInView();
        input.focus();
      });
      // Keyboard dismissed without the viewport telling us (hardware keyboard, etc.)
      input.addEventListener('blur', function() {
        setTimeout(function() {
          if (document.activeElement === input) return;
          chatWindow.style.height = '';
          chatWindow.style.top = '';
        }, 250);
      });
    }

    function onViewportChange() {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(function() {
        rafId = null;
        if (!chatWindow || !chatWindow.classList.contains('open')) return;

        var vv = window.visualViewport;
        var keyboardUp = vv.height < window.innerHeight - 120;
        if (!keyboardUp) {
          if (document.activeElement !== input) {
            chatWindow.style.height = '';
            chatWindow.style.top = '';
          }
          return;
        }

        // Fit the sheet to exactly what is visible, and remember it for next time
        chatWindow.style.height = vv.height + 'px';
        chatWindow.style.top = vv.offsetTop + 'px';
        if (!known || known.h !== Math.round(vv.height) || known.w !== window.innerWidth) {
          known = { w: window.innerWidth, h: Math.round(vv.height) };
          try { localStorage.setItem(KEYBOARD_KEY, JSON.stringify(known)); } catch (e) {}
        }
        keepLatestInView();
      });
    }

    window.visualViewport.addEventListener('resize', onViewportChange);
    window.visualViewport.addEventListener('scroll', onViewportChange);
  })();

  if (dock && isEmbedded) dock.disable();

  if (dock && !isEmbedded) {
    // The dock owns "is it open": it restores a chat left open on the last page, and
    // undocks when the window drops below 750px. Mirror whatever it decides.
    const syncDock = () => {
      if (dock.width) {
        ensureInitialized();
        chatWindow.classList.add('open');
        resizer.setAttribute('aria-valuenow', Math.round(dock.width / (window.innerWidth + dock.width) * 100));
      } else if (!overlay.classList.contains('open')) {
        chatWindow.classList.remove('open');
      }
    };
    document.addEventListener('dgchatdock', syncDock);
    syncDock();

    // Drag the left edge to resize, arrow keys when focused, double-click for the default.
    resizer.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      const rect = chatWindow.getBoundingClientRect();
      const grab = e.clientX - rect.left;
      let x = e.clientX;
      let frame = 0;
      const width = () => rect.right - (x - grab);
      const move = (ev) => {
        x = ev.clientX;
        if (!frame) frame = requestAnimationFrame(() => { frame = 0; dock.resize(width()); });
      };
      const end = () => {
        cancelAnimationFrame(frame);
        resizer.removeEventListener('pointermove', move);
        resizer.removeEventListener('pointerup', end);
        resizer.removeEventListener('pointercancel', end);
        resizer.classList.remove('dragging');
        document.documentElement.classList.remove('dg-chat-resizing');
        dock.resize(width(), true);
      };
      resizer.setPointerCapture(e.pointerId);
      resizer.classList.add('dragging');
      document.documentElement.classList.add('dg-chat-resizing');
      resizer.addEventListener('pointermove', move);
      resizer.addEventListener('pointerup', end);
      resizer.addEventListener('pointercancel', end);
    });
    resizer.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      dock.resize(dock.width + (e.key === 'ArrowLeft' ? 24 : -24), true);
    });
    resizer.addEventListener('dblclick', () => dock.reset());
  }

  // Expose global API so other scripts (e.g. Inquire button) can open the chat
  window.openDolphinChat = function(opts) {
    ensureInitialized();

    // Always start a fresh conversation
    if (opts && opts.inquiry) {
      startInquiry();
    } else {
      createNewConversation();
      renderConversationMenu();
    }

    // For floating (non-embedded) mode, slide the panel open
    if (!isEmbedded) {
      setOpen(true);
    }

    // Not on touch screens: focusing there raises the keyboard over the opening message
    if (input && !('ontouchstart' in window)) input.focus();
  };

  // Inquire buttons open the chat on a new conversation instead of the old pop-up form.
  // Listening in the capture phase keeps the theme's own handler (which shows that form)
  // from running. The button is a <span>, so it is also made reachable by keyboard.
  if (!isEmbedded) {
    const INQUIRE_BUTTON = '.contact_inquire';
    const inquireTarget = (e) => e.target && e.target.closest ? e.target.closest(INQUIRE_BUTTON) : null;

    document.querySelectorAll(INQUIRE_BUTTON).forEach((btn) => {
      btn.setAttribute('role', 'button');
      btn.setAttribute('tabindex', '0');
    });
    document.addEventListener('click', (e) => {
      if (!inquireTarget(e)) return;
      e.preventDefault();
      e.stopPropagation();
      window.openDolphinChat({ inquiry: true });
    }, true);
    document.addEventListener('keydown', (e) => {
      if ((e.key !== 'Enter' && e.key !== ' ') || !inquireTarget(e)) return;
      e.preventDefault();
      window.openDolphinChat({ inquiry: true });
    });
  }
})();
